export type PWAInstallSource = 'first_login' | 'settings' | 'post_game' | 'home_landing' | 'help_page' | 'splash_header' | 'badge';

export type PWAInstallEventType = 
  | 'modal_opened' 
  | 'install_clicked' 
  | 'install_success' 
  | 'install_declined' 
  | 'modal_dismissed' 
  | 'prompt_shown';

export interface PWAInstallEvent {
  event: PWAInstallEventType;
  platform: string;
  source: PWAInstallSource;
  canInstall: boolean;
  isInstalled: boolean;
  timestamp: number;
}

const STORAGE_KEY = 'wormble-pwa-analytics';
const MAX_EVENTS = 100;

// Platform detection
function detectPlatform(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'macOS';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
}

// Track PWA install event
export function trackPWAEvent(
  event: PWAInstallEventType,
  source: PWAInstallSource,
  canInstall: boolean = false,
  isInstalled: boolean = false
): void {
  try {
    const newEvent: PWAInstallEvent = {
      event,
      platform: detectPlatform(),
      source,
      canInstall,
      isInstalled,
      timestamp: Date.now(),
    };

    const existingEvents = getPWAEvents();
    const updatedEvents = [newEvent, ...existingEvents].slice(0, MAX_EVENTS);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedEvents));
    
    console.log(`[PWA Analytics] ${event} from ${source} (platform: ${newEvent.platform})`);
  } catch (error) {
    console.error('Failed to track PWA event:', error);
  }
}

// Get all PWA events
export function getPWAEvents(): PWAInstallEvent[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];
    return JSON.parse(data);
  } catch (error) {
    console.error('Failed to retrieve PWA events:', error);
    return [];
  }
}

// Get aggregated PWA analytics
export interface PWAAnalyticsSummary {
  totalEvents: number;
  totalModalOpens: number;
  totalInstallClicks: number;
  totalInstallSuccess: number;
  totalInstallDeclined: number;
  totalModalDismissed: number;
  conversionRate: number; // modal_opened -> install_success
  clickThroughRate: number; // modal_opened -> install_clicked
  sourceBreakdown: Record<PWAInstallSource, number>;
  platformBreakdown: Record<string, number>;
  recentEvents: PWAInstallEvent[];
}

export function getPWAAnalytics(): PWAAnalyticsSummary {
  const events = getPWAEvents();
  
  const totalModalOpens = events.filter(e => e.event === 'modal_opened').length;
  const totalInstallClicks = events.filter(e => e.event === 'install_clicked').length;
  const totalInstallSuccess = events.filter(e => e.event === 'install_success').length;
  const totalInstallDeclined = events.filter(e => e.event === 'install_declined').length;
  const totalModalDismissed = events.filter(e => e.event === 'modal_dismissed').length;
  
  const conversionRate = totalModalOpens > 0 
    ? (totalInstallSuccess / totalModalOpens) * 100 
    : 0;
  
  const clickThroughRate = totalModalOpens > 0 
    ? (totalInstallClicks / totalModalOpens) * 100 
    : 0;
  
  // Source breakdown
  const sourceBreakdown: Record<PWAInstallSource, number> = {
    first_login: 0,
    settings: 0,
    post_game: 0,
    home_landing: 0,
    help_page: 0,
    splash_header: 0,
    badge: 0,
  };
  
  events.forEach(event => {
    sourceBreakdown[event.source] = (sourceBreakdown[event.source] || 0) + 1;
  });
  
  // Platform breakdown
  const platformBreakdown: Record<string, number> = {};
  events.forEach(event => {
    platformBreakdown[event.platform] = (platformBreakdown[event.platform] || 0) + 1;
  });
  
  return {
    totalEvents: events.length,
    totalModalOpens,
    totalInstallClicks,
    totalInstallSuccess,
    totalInstallDeclined,
    totalModalDismissed,
    conversionRate,
    clickThroughRate,
    sourceBreakdown,
    platformBreakdown,
    recentEvents: events.slice(0, 10),
  };
}

// Clear PWA analytics
export function clearPWAAnalytics(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('[PWA Analytics] Analytics cleared');
  } catch (error) {
    console.error('Failed to clear PWA analytics:', error);
  }
}
